import { Injectable, NgZone } from '@angular/core';
import { ChatItem } from '../../../models/chat-ui.model';

/**
 * Encapsula la "pista flotante" que sigue al cursor sobre las respuestas del bot
 * (doble clic -> fuentes). Posee el listener `mousemove` registrado FUERA de Angular
 * (via `NgZone.runOutsideAngular`) y su limpieza, sacando ese bookkeeping del componente.
 *
 * El componente provee el elemento de la pista bajo demanda (puede ser `undefined`
 * hasta que el `@ViewChild` resuelve), por eso `show`/`hide`/`dispose` reciben el
 * elemento como parametro en lugar de retenerlo.
 */
@Injectable({
  providedIn: 'root'
})
export class ChatCursorHintService {
  /** Listener de mousemove para posicionar la pista junto al cursor (fuera de Angular). */
  private moveHandler?: (event: MouseEvent) => void;

  constructor(private ngZone: NgZone) {}

  /**
   * Muestra la pista junto al cursor cuando el usuario pasa el mouse sobre una respuesta
   * del bot que es seleccionable (system + id). Registra el seguimiento del cursor fuera
   * de Angular para no disparar deteccion de cambios.
   */
  show(el: HTMLElement | null | undefined, message: ChatItem | null | undefined, event: MouseEvent): void {
    if (!message || message.type !== 'system' || !message.id) {
      return;
    }
    if (!el) {
      return;
    }
    this.position(el, event.clientX, event.clientY);
    el.classList.add('visible');
    if (this.moveHandler) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.moveHandler = (move: MouseEvent) => this.position(el, move.clientX, move.clientY);
      document.addEventListener('mousemove', this.moveHandler, { passive: true });
    });
  }

  /** Oculta la pista y deja de seguir el cursor. */
  hide(el: HTMLElement | null | undefined): void {
    if (el) {
      el.classList.remove('visible');
    }
    this.dispose();
  }

  /** Quita el listener de mousemove si sigue registrado (idempotente). */
  dispose(): void {
    if (this.moveHandler) {
      document.removeEventListener('mousemove', this.moveHandler);
      this.moveHandler = undefined;
    }
  }

  private position(el: HTMLElement, x: number, y: number): void {
    el.style.transform = `translate3d(${x + 14}px, ${y + 16}px, 0)`;
  }
}
